import React, { useEffect, useRef, useState } from 'react';
import { InputMask, format } from '@react-input/mask';
import { useForm, useFormState } from 'react-final-form';
import { Icon } from '../../Icon';
import { scrollInputToCenter } from '../../../utils/helpers/common';
import {
  StyledPhoneMask,
  StyledTextField,
  StyledTextLabel,
  TextFieldContainer
} from './styled';
import { MaskedPhoneInputProps } from './types';

const PHONE_MASK_OPTIONS = {
  mask: '(0A_) ___-__-__',
  replacement: { A: /[1-9]/, _: /\d/ }
} as const;

const toDigits = (phone: string) => phone.replace(/[^\d]/g, '');

export const MaskedPhoneInput: React.FC<MaskedPhoneInputProps> = ({
  icon,
  name,
  onClick,
  disabled = false,
  placeholder,
  showPhoneMask = false
}) => {
  const { change } = useForm();
  const { values } = useFormState({
    subscription: { values: true }
  });
  const value = values[name] === undefined ? '' : values[name];
  const digits = toDigits(String(value));
  const formattedValue = digits ? format(digits, PHONE_MASK_OPTIONS) : digits;
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Contacts may write API-formatted phones (e.g. "(050) 210-29-82") into
  // form state; keep digits-only so validation/submit work for disabled fields.
  useEffect(() => {
    if (value !== '' && value !== digits) {
      change(name, digits);
    }
  }, [value, digits, name, change]);

  const focusInput = () => {
    setIsFocused(true);
    inputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    change(name, toDigits(e.target.value));
  };

  const scrollHandler = () => scrollInputToCenter(inputRef.current);

  const phoneMask = '(000) 000-00-00';

  return (
    <TextFieldContainer data-cy="text-field-container">
      <StyledTextField onClick={onClick} data-cy="text-field">
        <StyledTextLabel
          role="presentation"
          onClick={focusInput}
          $isActive={isFocused || digits}
        >
          {placeholder}
        </StyledTextLabel>
        <InputMask
          {...PHONE_MASK_OPTIONS}
          ref={inputRef}
          pattern="[0-9]*"
          autoComplete="off"
          inputMode="numeric"
          disabled={disabled}
          data-cy="phone-input"
          onFocus={() => {
            setIsFocused(true);
            scrollHandler();
          }}
          onBlur={() => setIsFocused(false)}
          onChange={handleInputChange}
          className="masked-input-field"
          value={digits ? formattedValue : digits}
        />
        {showPhoneMask && !digits && isFocused && (
          <StyledPhoneMask tag="body_1">{phoneMask}</StyledPhoneMask>
        )}
      </StyledTextField>

      {icon && (
        <Icon IconComponent={icon} width={35} height={35} indent={false} />
      )}
    </TextFieldContainer>
  );
};
